import * as Cesium from "cesium";

export async function generateAutoLaneletRoute({
  viewer,
  routeDS,
  laneletDS,
  virtualDS,
  markerDS,
  turningDS,
  turningArcDS,
  onDone,
  onAbort,
  verbose = false,
  radiusStart = 3.0, // m
  radiusHit = 3.0, // m
  widthGreen = 10, // px
}) {
  // validate inputs
  if (!viewer || !routeDS || !laneletDS || !virtualDS || !markerDS || (!turningDS && !turningArcDS) || typeof onDone !== "function") {
    throw new Error("viewer, routeDS, laneletDS, virtualDS, markerDS, (turningDS or turningArcDS), onDone required");
  }

  // debug helper (prints only when verbose=true)
  const dbg = (code, info = {}) => {
    if (!verbose) return;
    try {
      console.warn(`[lanelet][${code}]`, info);
    } catch {}
  };

  const now = () => viewer.clock.currentTime;
  let _lastEntity = null;
  let stopReason = null;

  function focusEntity(e) {
    try {
      const p = e?.polyline?.positions?.getValue(now());
      if (!p?.length) return;
      const bs = Cesium.BoundingSphere.fromPoints(p);
      viewer?.camera?.flyToBoundingSphere(bs, {
        duration: 1.2,
        offset: new Cesium.HeadingPitchRange(0, -0.6, bs.radius * 3),
      });
    } catch {}
  }

  // abort helper attaches styled lanelet points if available
  function abort(reason, details) {
    stopReason = reason;
    verbose && console.warn(reason, details || {});

    // try to attach styled lanelet points (safe)
    let extra = { lanelet_points: [], lanelet_points_geojson: { type: "FeatureCollection", features: [] } };
    try {
      if (typeof buildStyledLaneletPoints === "function") {
        const built = buildStyledLaneletPoints();
        extra.lanelet_points = built.points;
        extra.lanelet_points_geojson = built.geojson;
      }
    } catch (e) {
      // ignore
    }

    if (_lastEntity) focusEntity(_lastEntity);
    onAbort?.(reason, Object.assign({}, details || {}, extra));
    return { clear: clearStyles, stopReason };
  }

  const entsRoute = routeDS.entities?.values || [];
  const entsLane = laneletDS.entities?.values || [];
  const entsVirtual = virtualDS.entities?.values || [];
  const entsMarker = markerDS.entities?.values || [];
  const entsTurning = turningDS.entities?.values || [];
  const entsTurningArc = turningArcDS?.entities?.values || [];

  if (!entsRoute.length || !entsLane.length || !entsVirtual.length || !entsMarker.length || (!entsTurning.length && !entsTurningArc.length)) {
    return abort("Missing required data sources");
  }

  // collect route points (dedup)
  const routePoints = (() => {
    const out = [];
    for (const e of entsRoute) {
      const p = e?.polyline?.positions?.getValue(now());
      if (!p?.length) continue;
      for (const q of p) out.push(q);
    }
    const uniq = [];
    const toEN = makeENU(out[0] || Cesium.Cartesian3.ZERO).toEN;
    for (const q of out) {
      if (!uniq.length) {
        uniq.push(q);
        continue;
      }
      const a = toEN(uniq[uniq.length - 1]),
        b = toEN(q);
      if (hypot2D(a, b) > 0.2) uniq.push(q);
    }
    return uniq;
  })();
  if (!routePoints.length) return abort("No route points found");

  // ENU (2D)
  function makeENU(anchorCartesian) {
    const anchorCart = Cesium.Cartographic.fromCartesian(anchorCartesian);
    // keep anchor height as-is (not used elsewhere for distances) but cartToLonLat will read heights from actual points
    const origin = Cesium.Ellipsoid.WGS84.cartographicToCartesian(new Cesium.Cartographic(anchorCart.longitude, anchorCart.latitude, anchorCart.height || 0));
    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(origin);
    const inv = Cesium.Matrix4.inverseTransformation(enu, new Cesium.Matrix4());
    const scratch = new Cesium.Cartesian3();
    function toEN(c) {
      Cesium.Matrix4.multiplyByPoint(inv, c, scratch);
      return { x: scratch.x, y: scratch.y };
    }
    function dist2D(a, b) {
      const A = toEN(a),
        B = toEN(b);
      return Math.hypot(A.x - B.x, A.y - B.y);
    }
    function same2D(a, b, eps = 0.007) {
      return dist2D(a, b) < eps;
    }
    return { toEN, dist2D, same2D };
  }
  const { toEN, dist2D, same2D } = makeENU(routePoints[0]);
  const EPSM = 0.007;

  // helpers
  const posArr = (e) => e?.polyline?.positions?.getValue(now());
  const oneWay = (e) => {
    const p = e?.properties;
    const v = p?.one_way?.getValue?.(now());
    return v !== false;
  };
  const getId = (e) => {
    const p = e?.properties;
    const pid = p?.id?.getValue?.(now());
    return pid ?? e?.id ?? String(e?._id ?? Math.random());
  };
  const getProp = (e, k) => e?.properties?.[k]?.getValue?.(now());
  function segLenMeters(e) {
    const p = posArr(e);
    if (!p || p.length < 2) return 0;
    let s = 0;
    for (let i = 1; i < p.length; i++) s += dist2D(p[i - 1], p[i]);
    return s;
  }
  const first = (e) => {
    const p = posArr(e);
    return p && p[0];
  };
  const last = (e) => {
    const p = posArr(e);
    return p && p[p.length - 1];
  };

  function connectionReversed(curEnd, nextE) {
    const f = first(nextE),
      l = last(nextE);
    if (!f || !l) return null;
    if (same2D(curEnd, f, EPSM)) return false;
    if (!oneWay(nextE) && same2D(curEnd, l, EPSM)) return true;
    return null;
  }

  // return [lon, lat, z>=0]
  function cartToLonLat(c) {
    const cc = Cesium.Cartographic.fromCartesian(c);
    const lon = Cesium.Math.toDegrees(cc.longitude);
    const lat = Cesium.Math.toDegrees(cc.latitude);
    const z = Math.max(0, cc.height || 0); // clamp negative heights to 0
    return [lon, lat, z];
  }

  function pointPolylineDistance2D(p, arr) {
    const P = toEN(p);
    let best = Infinity;
    for (let i = 1; i < arr.length; i++) {
      const A = toEN(arr[i - 1]),
        B = toEN(arr[i]);
      const abx = B.x - A.x,
        aby = B.y - A.y,
        apx = P.x - A.x,
        apy = P.y - A.y;
      const ab2 = abx * abx + aby * aby;
      let t = ab2 > 0 ? (apx * abx + apy * aby) / ab2 : 0;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
      const px = A.x + t * abx,
        py = A.y + t * aby;
      const d = Math.hypot(P.x - px, P.y - py);
      if (d < best) best = d;
      if (best === 0) break;
    }
    return best;
  }

  // spatial index
  const cellSize = 5.0;
  const cellKey = (c) => {
    const en = toEN(c);
    return `${Math.floor(en.x / cellSize)}|${Math.floor(en.y / cellSize)}`;
  };
  const nearCells = (p, r) => {
    const en = toEN(p);
    const dx = Math.ceil(r / cellSize);
    const cx = Math.floor(en.x / cellSize),
      cy = Math.floor(en.y / cellSize);
    const keys = [];
    for (let ix = -dx; ix <= dx; ix++) for (let iy = -dx; iy <= dx; iy++) keys.push(`${cx + ix}|${cy + iy}`);
    return keys;
  };

  // lanelets
  const L = entsLane.filter((e) => e?.polyline && posArr(e)?.length >= 2);
  if (!L.length) return abort("No lanelets found");

  // side-line index (virtual, marker, turning)
  const sideIndex = new Map();
  function indexSideDS(arr) {
    for (const e of arr) {
      const id = getId(e),
        p = posArr(e);
      if (!id || !p?.length || p.length < 2) continue;
      sideIndex.set(String(id), e);
    }
  }
  indexSideDS(entsVirtual);
  indexSideDS(entsMarker);
  indexSideDS(entsTurning);
  indexSideDS(entsTurningArc);

  // lane meta + indices
  const laneMeta = new Map();
  const nodeKeyEps = 0.05;
  const nodeKey = (c) => {
    const en = toEN(c);
    return `${Math.round(en.x / nodeKeyEps)}~${Math.round(en.y / nodeKeyEps)}`;
  };
  const nodeToLanelets = new Map();
  const segIndex = new Map();

  function indexPolyline(id, arr) {
    for (const v of arr) {
      const ck = cellKey(v);
      if (!segIndex.has(ck)) segIndex.set(ck, new Set());
      segIndex.get(ck).add(id);
    }
    const sampleStep = Math.max(1.0, Math.min(3.0, radiusStart));
    for (let i = 1; i < arr.length; i++) {
      const a = arr[i - 1],
        b = arr[i],
        Lseg = dist2D(a, b);
      const n = Math.max(1, Math.ceil(Lseg / sampleStep));
      for (let k = 1; k <= n - 1; k++) {
        const t = k / n;
        const p = Cesium.Cartesian3.lerp(a, b, t, new Cesium.Cartesian3());
        const ck = cellKey(p);
        if (!segIndex.has(ck)) segIndex.set(ck, new Set());
        segIndex.get(ck).add(id);
      }
    }
  }

  for (const e of L) {
    const id = getId(e),
      arr = posArr(e);
    const start = arr[0],
      end = arr[arr.length - 1],
      len = segLenMeters(e),
      ow = oneWay(e);

    let minX = +Infinity,
      maxX = -Infinity,
      minY = +Infinity,
      maxY = -Infinity;
    for (const v of arr) {
      const en = toEN(v);
      if (en.x < minX) minX = en.x;
      if (en.x > maxX) maxX = en.x;
      if (en.y < minY) minY = en.y;
      if (en.y > maxY) maxY = en.y;
    }
    const bx = { minX, maxX, minY, maxY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, r: Math.hypot((maxX - minX) / 2, (maxY - minY) / 2) };

    laneMeta.set(id, { e, id, len, oneWay: ow, start, end, pos: arr, bbox: bx });

    const nkS = nodeKey(start),
      nkE = nodeKey(end);
    if (!nodeToLanelets.has(nkS)) nodeToLanelets.set(nkS, []);
    if (!nodeToLanelets.has(nkE)) nodeToLanelets.set(nkE, []);
    nodeToLanelets.get(nkS).push({ id, at: "start" });
    nodeToLanelets.get(nkE).push({ id, at: "end" });

    indexPolyline(id, arr);
  }

  function bboxDistance2D(p, box) {
    const en = toEN(p),
      x = en.x,
      y = en.y;
    const dx = x < box.minX ? box.minX - x : x > box.maxX ? x - box.maxX : 0;
    const dy = y < box.minY ? box.minY - y : y > box.maxY ? y - box.maxY : 0;
    return Math.hypot(dx, dy);
  }

  // project point onto polyline in ENU (returns EN coords and dist)
  function projectPointOntoPolylineEN(pCartesian, arrCartesian) {
    const P = toEN(pCartesian);
    let best = { x: 0, y: 0, dist: Infinity };
    for (let i = 1; i < arrCartesian.length; i++) {
      const A = toEN(arrCartesian[i - 1]),
        B = toEN(arrCartesian[i]);
      const abx = B.x - A.x,
        aby = B.y - A.y,
        apx = P.x - A.x,
        apy = P.y - A.y;
      const ab2 = abx * abx + aby * aby;
      let t = ab2 > 0 ? (apx * abx + apy * aby) / ab2 : 0;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
      const px = A.x + t * abx,
        py = A.y + t * aby;
      const d = Math.hypot(P.x - px, P.y - py);
      if (d < best.dist) {
        best = { x: px, y: py, dist: d };
        if (d === 0) break;
      }
    }
    return best;
  }

  function candidateLaneletsNearPoint(p, radius) {
    // collect nearby lanelet ids by grid/bbox
    const bag = new Set();
    for (const k of nearCells(p, radius + cellSize)) segIndex.get(k)?.forEach((id) => bag.add(id));
    if (bag.size === 0) {
      for (const [id, meta] of laneMeta) {
        if (bboxDistance2D(p, meta.bbox) <= radius + cellSize) bag.add(id);
      }
    }
    const prelim = [];
    bag.forEach((id) => {
      const meta = laneMeta.get(id);
      const d = pointPolylineDistance2D(p, meta.pos);
      if (d <= radius) prelim.push({ id, d });
    });
    prelim.sort((a, b) => a.d - b.d);

    // detailed filtering with reasons
    const filtered = [];
    const dropped = []; // for logging reasons

    const EPS_AA = 0.01;
    for (const c of prelim) {
      const meta = laneMeta.get(c.id);
      const rid = getProp(meta.e, "right_line_id");
      const lid = getProp(meta.e, "left_line_id");
      const rEnt = rid != null ? sideIndex.get(String(rid)) : undefined;
      const lEnt = lid != null ? sideIndex.get(String(lid)) : undefined;

      if (!rEnt || !lEnt) {
        dropped.push({
          id: c.id,
          reason: !rEnt && !lEnt ? "missing-both-sides" : !rEnt ? "missing-right" : "missing-left",
          radius,
          d: c.d,
        });
        continue;
      }
      const rPos = posArr(rEnt);
      const lPos = posArr(lEnt);
      if (!rPos?.length || !lPos?.length) {
        dropped.push({ id: c.id, reason: "empty-side-geometry", radius, d: c.d });
        continue;
      }

      // project for aa/bb
      const PR = projectPointOntoPolylineEN(p, rPos);
      const PL = projectPointOntoPolylineEN(p, lPos);

      let aa = Math.hypot(PR.x - PL.x, PR.y - PL.y);
      if (aa < EPS_AA) aa = EPS_AA;
      const bb = PR.dist + PL.dist;

      if (bb <= 1.05 * aa) {
        filtered.push(c);
      } else {
        // Persian console message + numeric details
        dbg("aa-bb-fail", {
          laneletId: c.id,
          msg: "aa/bb condition failed (bb must be ≤ 1.05×aa)",
          aa,
          bb,
          ratio: bb / aa,
          radius,
          pointLLH: cartToLonLat(p),
        });
        dropped.push({ id: c.id, reason: "aa-bb-fail", aa, bb, ratio: bb / aa, radius });
      }
    }

    // high-level summary logs
    dbg("candidate-scan", {
      msg: filtered.length ? "Valid candidate lanelet(s) found" : "No candidate lanelets found within radius or all were filtered out",
      radius,
      totalNearby: bag.size,
      inRadius: prelim.length,
      passedFilter: filtered.length,
      dropped,
      pointLLH: cartToLonLat(p),
    });

    return filtered;
  }

  // styling + reset
  const styledLane = new Set(),
    originalLane = new Map();
  const styledSide = new Set(),
    originalSide = new Map();

  function getWidthNumber(w) {
    const t = now();
    return w && typeof w.getValue === "function" ? w.getValue(t) : typeof w === "number" ? w : 1;
  }

  function styleLane(laneId) {
    if (styledLane.has(laneId)) return;
    const meta = laneMeta.get(laneId);
    if (!meta?.e?.polyline) return;
    const pl = meta.e.polyline;
    if (!originalLane.has(laneId)) originalLane.set(laneId, { material: pl.material, width: getWidthNumber(pl.width) });
    pl.material = Cesium.Color.GREEN;
    if (pl.width?.setValue) pl.width.setValue(widthGreen);
    else pl.width = widthGreen;
    styledLane.add(laneId);
    _lastEntity = laneMeta.get(laneId)?.e;
  }

  function styleSide(sideId, isRight) {
    if (!sideId) return;
    const sid = String(sideId);
    if (styledSide.has(sid)) return;
    const ent = sideIndex.get(sid);
    if (!ent?.polyline) return;
    const pl = ent.polyline;
    if (!originalSide.has(sid)) originalSide.set(sid, { material: pl.material, width: getWidthNumber(pl.width) });
    pl.material = isRight ? Cesium.Color.BLUE : Cesium.Color.PURPLE;
    if (pl.width?.setValue) pl.width.setValue(widthGreen);
    else pl.width = widthGreen;
    styledSide.add(sid);
    _lastEntity = sideIndex.get(String(sideId));
  }

  function clearStyles() {
    for (const [laneId, prev] of originalLane) {
      const pl = laneMeta.get(laneId)?.e?.polyline;
      if (!pl) continue;
      pl.material = prev.material;
      if (pl.width?.setValue) pl.width.setValue(prev.width);
      else pl.width = prev.width ?? 1;
    }
    originalLane.clear();
    styledLane.clear();
    for (const [sid, prev] of originalSide) {
      const pl = sideIndex.get(sid)?.polyline;
      if (!pl) continue;
      pl.material = prev.material;
      if (pl.width?.setValue) pl.width.setValue(prev.width);
      else pl.width = prev.width ?? 1;
    }
    originalSide.clear();
    styledSide.clear();
    if (viewer?.scene?.requestRenderMode) viewer.scene.requestRender();
  }

  // midpoint cartesian for polyline
  function midpointCartesianForPolyline(arr) {
    if (!arr || arr.length === 0) return arr?.[0] ?? Cesium.Cartesian3.ZERO;
    let total = 0,
      segs = [];
    for (let i = 1; i < arr.length; i++) {
      const d = dist2D(arr[i - 1], arr[i]);
      segs.push(d);
      total += d;
    }
    if (total === 0) return arr[0];
    let acc = 0,
      half = total / 2,
      mid = arr[0];
    for (let i = 0; i < segs.length; i++) {
      if (acc + segs[i] >= half) {
        const t = (half - acc) / segs[i];
        mid = Cesium.Cartesian3.lerp(arr[i], arr[i + 1], t, new Cesium.Cartesian3());
        break;
      }
      acc += segs[i];
    }
    return mid;
  }

  // build geojson of currently styled lanelets (points include z)
  function buildStyledLaneletPoints() {
    const points = [];
    const features = [];
    for (const laneId of styledLane) {
      const meta = laneMeta.get(laneId);
      if (!meta) continue;
      const midCart = midpointCartesianForPolyline(meta.pos);
      const llz = cartToLonLat(midCart); // [lon, lat, z>=0]
      points.push({ id: laneId, coord: llz });
      features.push({
        type: "Feature",
        properties: { id: laneId },
        geometry: { type: "Point", coordinates: [llz[0], llz[1], llz[2]] },
      });
    }
    return { points, geojson: { type: "FeatureCollection", features } };
  }

  // multi-start
  const r0 = routePoints[0];
  const startCands = candidateLaneletsNearPoint(r0, radiusStart);
  dbg("start-candidates", {
    msg: startCands.length ? "Candidate lanelet(s) found near route start" : "No lanelet found near route start within radius",
    count: startCands.length,
    radiusStart,
    startLLH: cartToLonLat(r0),
  });
  if (!startCands.length) return abort("No lanelet within 3m of route start", { r0LL: cartToLonLat(r0) });

  const startStates = [];
  for (const c of startCands) {
    const m = laneMeta.get(c.id);
    startStates.push({ id: c.id, rev: false });
    if (!m.oneWay) startStates.push({ id: c.id, rev: true });
  }

  // graph helpers
  function nextFrom(laneId, rev) {
    const cur = laneMeta.get(laneId);
    const curEnd = rev ? cur.start : cur.end;
    const nk = nodeKey(curEnd);
    const others = nodeToLanelets.get(nk);
    if (!others) return [];
    const out = [];
    for (const o of others) {
      if (o.id === laneId) continue;
      const nx = laneMeta.get(o.id);
      const rev2 = connectionReversed(curEnd, nx.e);
      if (rev2 === null) continue;
      out.push({ id: o.id, rev: !!rev2, cost: nx.len });
    }
    return out;
  }

  function aStarToTargets(startState, targetCheck, heuristic) {
    const keyState = (id, rev) => `${id}|${rev ? 1 : 0}`;
    class Heap {
      constructor() {
        this.a = [];
      }
      push(n) {
        this.a.push(n);
        this._up(this.a.length - 1);
      }
      pop() {
        if (!this.a.length) return null;
        const r = this.a[0],
          e = this.a.pop();
        if (this.a.length) {
          this.a[0] = e;
          this._down(0);
        }
        return r;
      }
      _up(i) {
        const a = this.a;
        while (i > 0) {
          const p = (i - 1) >> 1;
          if (a[p].f <= a[i].f) break;
          [a[p], a[i]] = [a[i], a[p]];
          i = p;
        }
      }
      _down(i) {
        const a = this.a,
          n = a.length;
        for (;;) {
          let l = i * 2 + 1,
            r = i * 2 + 2,
            m = i;
          if (l < n && a[l].f < a[m].f) m = l;
          if (r < n && a[r].f < a[m].f) m = r;
          if (m === i) break;
          [a[m], a[i]] = [a[i], a[m]];
          i = m;
        }
      }
      get size() {
        return this.a.length;
      }
    }
    const open = new Heap(),
      g = new Map(),
      came = new Map();
    const startKey = keyState(startState.id, startState.rev);
    const startEnd = startState.rev ? laneMeta.get(startState.id).start : laneMeta.get(startState.id).end;
    open.push({ key: startKey, id: startState.id, rev: startState.rev, g: 0, f: heuristic(startEnd) });
    g.set(startKey, 0);
    const closed = new Set();

    while (open.size) {
      const cur = open.pop();
      if (closed.has(cur.key)) continue;
      closed.add(cur.key);
      const curEnd = cur.rev ? laneMeta.get(cur.id).start : laneMeta.get(cur.id).end;
      if (targetCheck(cur.id, cur.rev, curEnd)) {
        const path = [];
        let k = cur.key;
        while (k && k !== startKey) {
          const step = came.get(k);
          path.push({ id: step.id, rev: step.rev });
          k = step.prev;
        }
        path.push({ id: startState.id, rev: startState.rev });
        path.reverse();
        return path;
      }
      for (const nb of nextFrom(cur.id, cur.rev)) {
        const nbKey = keyState(nb.id, nb.rev);
        const tentative = cur.g + nb.cost;
        if (g.has(nbKey) && tentative >= g.get(nbKey)) continue;
        g.set(nbKey, tentative);
        const nbEnd = nb.rev ? laneMeta.get(nb.id).start : laneMeta.get(nb.id).end;
        const f = tentative + heuristic(nbEnd);
        came.set(nbKey, { prev: cur.key, id: nb.id, rev: nb.rev });
        open.push({ key: nbKey, id: nb.id, rev: nb.rev, g: tentative, f });
      }
    }
    return null;
  }

  // connect to second point
  const r1 = routePoints[1] || routePoints[0];
  const targetsR1 = new Set(candidateLaneletsNearPoint(r1, radiusHit).map((x) => x.id));
  const targetCheckStart = (id, rev, end) => targetsR1.has(id) || dist2D(end, r1) <= radiusHit;
  const heuristicStart = (pt) => dist2D(pt, r1);

  let fullPath = null;
  for (const st of startStates) {
    const seg = aStarToTargets(st, targetCheckStart, heuristicStart);
    if (seg) {
      fullPath = seg.slice();
      break;
    }
  }
  if (!fullPath) {
    dbg("astar-no-path-start", {
      msg: "A* could not reach the second point from the start",
      radiusHit,
      r0LLH: cartToLonLat(r0),
      r1LLH: cartToLonLat(r1),
    });
  }
  if (!fullPath) {
    const bestStartId = startCands[0].id;
    styleLane(bestStartId);
    return abort("No path from any start to second route point");
  }

  // side existence + styling
  function mustGetSide(id, sideLabel, laneletId) {
    const sid = id != null ? String(id) : "";
    const ent = sid ? sideIndex.get(sid) : undefined;
    if (!ent) {
      abort(`missing-${sideLabel}-line`, { laneletId, sideId: id }); // call once
      return null; // signal failure
    }
    return ent;
  }

  function styleSidesForStep(step) {
    const m = laneMeta.get(step.id);
    const rid = getProp(m.e, "right_line_id");
    const lid = getProp(m.e, "left_line_id");

    const rEnt = mustGetSide(rid, "right", m.id);
    if (!rEnt) return false; // stop here

    const lEnt = mustGetSide(lid, "left", m.id);
    if (!lEnt) return false; // stop here

    styleSide(String(rid), true);
    styleSide(String(lid), false);
    return true;
  }

  for (const s of fullPath) {
    styleLane(s.id);
    if (!styleSidesForStep(s)) return { clear: clearStyles, stopReason }; // single abort
  }

  // extend along route
  for (let i = 1; i < routePoints.length - 1; i++) {
    const targetP = routePoints[i + 1];
    const targets = candidateLaneletsNearPoint(targetP, radiusHit).map((c) => c.id);

    if (!targets.length) {
      dbg("no-candidate-in-radius", {
        msg: "No lanelet found within the defined radius",
        atIndex: i + 1,
        radiusHit,
        pointLLH: cartToLonLat(targetP),
      });
      return abort(`no-lanelet-near-route-point-${i + 1}`);
    }
    const targetSet = new Set(targets);
    const lastState = fullPath[fullPath.length - 1];

    const targetCheck = (id, rev, end) => targetSet.has(id) || dist2D(end, targetP) <= radiusHit;
    const heuristic = (pt) => dist2D(pt, targetP);

    const seg = aStarToTargets(lastState, targetCheck, heuristic);

    if (!seg) {
      dbg("astar-no-path", {
        msg: "A* could not find a way between two path points",
        fromIndex: i,
        toIndex: i + 1,
        radiusHit,
        fromLLH: cartToLonLat(routePoints[i]),
        toLLH: cartToLonLat(targetP),
        lastState: fullPath[fullPath.length - 1],
      });
      return abort(`no-path-between-points-${i}-to-${i + 1}`);
    }

    for (let k = 1; k < seg.length; k++) {
      fullPath.push(seg[k]);
      styleLane(seg[k].id);
      if (!styleSidesForStep(seg[k])) return { clear: clearStyles, stopReason };
    }
  }

  // side connectivity checks
  function checkSideConnectivity(side, curStep, nextStep) {
    const curMeta = laneMeta.get(curStep.id),
      nextMeta = laneMeta.get(nextStep.id);
    const joint = curStep.rev ? curMeta.start : curMeta.end;

    const curSideId = getProp(curMeta.e, `${side}_line_id`);
    const nextSideId = getProp(nextMeta.e, `${side}_line_id`);
    const curEnt = sideIndex.get(String(curSideId));
    const nextEnt = sideIndex.get(String(nextSideId));

    if (!curEnt || !nextEnt) {
      dbg("side-missing", {
        msg: `${side} side line not found`,
        stepPair: [curMeta.id, nextMeta.id],
        curSideId,
        nextSideId,
      });
      abort(`missing-${side}-line`, { stepPair: [curMeta.id, nextMeta.id], curSideId, nextSideId });
      return false;
    }

    const touchesCur = (first(curEnt) && same2D(joint, first(curEnt), EPSM)) || (last(curEnt) && same2D(joint, last(curEnt), EPSM));

    if (!touchesCur) {
      dbg("side-not-touching-joint", {
        msg: `${side} side line does not touch the joint point`,
        sideId: curSideId,
        jointLLH: cartToLonLat(joint),
      });
      abort(`side-line-not-touching-joint-${side}`, { curSideId, jointLL: cartToLonLat(joint) });
      return false;
    }

    const rev2 = connectionReversed(joint, nextEnt);
    if (rev2 === null) {
      dbg("side-disconnected", {
        msg: `${side} side line not connected to next segment`,
        nextSideId,
        jointLLH: cartToLonLat(joint),
      });
      abort(`side-line-disconnected-${side}`, { nextSideId, jointLL: cartToLonLat(joint) });
      return false;
    }

    return true;
  }

  for (let i = 0; i < fullPath.length - 1; i++) {
    const a = fullPath[i],
      b = fullPath[i + 1];
    if (!checkSideConnectivity("right", a, b)) return { clear: clearStyles, stopReason };
    if (!checkSideConnectivity("left", a, b)) return { clear: clearStyles, stopReason };
  }

  for (let i = 0; i < fullPath.length - 1; i++) {
    const a = fullPath[i],
      b = fullPath[i + 1];
    if (!checkSideConnectivity("right", a, b)) return { clear: clearStyles, stopReason };
    if (!checkSideConnectivity("left", a, b)) return { clear: clearStyles, stopReason };
  }

  // dedupe
  const compressed = [];
  for (const step of fullPath) {
    if (!compressed.length || compressed[compressed.length - 1].id !== step.id || compressed[compressed.length - 1].rev !== step.rev) compressed.push(step);
  }
  fullPath = compressed;

  // geojson (LineString) with z included
  const miles = fullPath.reduce((s, st) => s + laneMeta.get(st.id).len, 0) / 1609.344;
  const coordsLL = [];
  const firstStep = fullPath[0],
    firstMeta = laneMeta.get(firstStep.id);
  coordsLL.push(cartToLonLat(firstStep.rev ? firstMeta.end : firstMeta.start)); // [lon,lat,z]
  for (const st of fullPath) {
    const m = laneMeta.get(st.id),
      arr = m.pos;
    let total = 0,
      segs = [];
    for (let i = 1; i < arr.length; i++) {
      const d = dist2D(arr[i - 1], arr[i]);
      segs.push(d);
      total += d;
    }
    let acc = 0,
      mid = arr[0],
      half = total / 2;
    for (let i = 0; i < segs.length; i++) {
      if (acc + segs[i] >= half) {
        const t = (half - acc) / segs[i];
        mid = Cesium.Cartesian3.lerp(arr[i], arr[i + 1], t, new Cesium.Cartesian3());
        break;
      }
      acc += segs[i];
    }
    coordsLL.push(cartToLonLat(mid));
  }
  const lastStep = fullPath[fullPath.length - 1],
    lastMeta = laneMeta.get(lastStep.id);
  coordsLL.push(cartToLonLat(lastStep.rev ? lastMeta.start : lastMeta.end));

  const geojson = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {
          lanelet_ids: fullPath.map((s) => s.id),
          number_of_lanelets: fullPath.length,
          length_miles: miles,
          stopReason: "completed",
        },
        // include z in coordinates (lon, lat, z)
        geometry: { type: "LineString", coordinates: coordsLL.map(([lon, lat, z]) => [lon, lat, Math.max(0, z)]) },
      },
    ],
  };

  // build styled-lanelet points for final callback (includes z)
  const styledBuilt = buildStyledLaneletPoints();

  stopReason = "completed";
  verbose && console.debug("[lanelet] segments:", fullPath.length, "miles:", miles.toFixed(3));
  if (_lastEntity) focusEntity(_lastEntity);
  onDone?.(
    Object.assign({}, { coordinates: coordsLL, ids: fullPath.map((s) => s.id), geojson, miles, stopReason }, { lanelet_points: styledBuilt.points, lanelet_points_geojson: styledBuilt.geojson })
  );

  return { clear: clearStyles, stopReason };

  // tiny helper
  function hypot2D(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }
}
